/**
 * OpenTelemetry W3C Trace Context Propagation Utility
 *
 * This module provides utilities for propagating W3C trace context between
 * the backend (Elixir/Phoenix) and frontend (React/Apollo) applications.
 *
 * The backend sends `traceparent` headers via TraceContextResponsePlug, and
 * this utility captures and propagates them back in subsequent requests to
 * maintain trace continuity across the client-server boundary.
 *
 * W3C Trace Context spec: https://www.w3.org/TR/trace-context/
 */

// current trace context.
let currentTraceParent: string | null = null
let currentTraceState: string | null = null

/**
 * Initialize trace context from meta tag on page load.
 * This captures the traceparent from the initial HTML page response,
 * allowing frontend API requests to continue the trace started by the page load.
 */
function initializeFromMetaTag(): void {
  if (typeof document !== 'undefined') {
    const metaTag = document.querySelector('meta[name="traceparent"]')
    if (metaTag) {
      const traceparent = metaTag.getAttribute('content')
      if (traceparent) {
        currentTraceParent = traceparent
      }
    }
  }
}

// Initialize on module load
initializeFromMetaTag()

/**
 * Update the stored trace context from response headers.
 * This should be called when receiving responses from the backend.
 */
export function updateTraceContext(headers: Headers): void {
  const traceparent = headers.get('traceparent')
  const tracestate = headers.get('tracestate')

  if (traceparent) {
    currentTraceParent = traceparent
  }

  if (tracestate) {
    currentTraceState = tracestate
  }
}

/**
 * Get the current trace context headers to include in outgoing requests.
 */
export function getTraceContextHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}

  if (currentTraceParent) {
    headers['traceparent'] = currentTraceParent
  }

  if (currentTraceState) {
    headers['tracestate'] = currentTraceState
  }

  return headers
}

/**
 * Clear the stored trace context.
 * Useful for testing or when explicitly starting a new trace.
 */
export function clearTraceContext(): void {
  currentTraceParent = null
  currentTraceState = null
}

/**
 * Get the current traceparent value (for debugging/logging)
 */
export function getCurrentTraceParent(): string | null {
  return currentTraceParent
}
